/**
 * Planner (Blueprint v2 P7): decompose a task into a minimal task DAG via DeepSeek
 * (thinking), or ask to clarify when too ambiguous. Pure `scheduleByDeps` batches
 * the DAG by dependency tags for a bounded worker pool.
 *
 * On top of the DAG sits the model- and effort-aware BUILD PLAN
 * ({@link draftBuildPlan}): research steps, tooling candidates per decision area
 * (resolved by selection.ts), and build tasks — its depth scaled by the budget
 * (effort × value, see budget.ts) and the executing model's profile. The engine
 * executes against this plan instead of improvising tool choices mid-build.
 */
import { createAiClient, DEEPSEEK_PRO_MODEL } from '@fim/ai-sdk';
import { effortToReasoning, type EffortLevel, type Tier } from '@fim/futurex-protocol';
import type { Budget } from './budget.js';
import type { ChatClient, SessionGoal } from './goal.js';
import type { ModelProfile } from './planning-config.js';
import type { SelectionResult } from './selection.js';
import type { ValueAssessment } from './value.js';

export interface Task {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly dependsOn: readonly string[];
  /** Files / areas this task is expected to touch. */
  readonly targets: readonly string[];
}

export interface Plan {
  readonly summary: string;
  readonly tasks: readonly Task[];
}

export type PlanResult =
  | { readonly kind: 'plan'; readonly plan: Plan }
  | { readonly kind: 'clarify'; readonly questions: readonly string[] };

const PLANNER_SYSTEM = [
  'You are a senior engineer decomposing a coding task into a minimal task DAG.',
  'Respond with ONLY one minified JSON object — no prose, no code fence — that is EITHER',
  '{"kind":"plan","plan":{"summary":string,"tasks":[{"id":string,"title":string,"description":string,"dependsOn":string[],"targets":string[]}]}}',
  'OR {"kind":"clarify","questions":string[]} when the task is too ambiguous to plan.',
  'Keep tasks coarse (3-8), give each a short stable id, and set dependsOn to other task ids.',
].join('\n');

function asStrings(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
}

function normalizeTask(v: unknown, i: number): Task | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const title = typeof o.title === 'string' ? o.title : '';
  if (!title) return null;
  return {
    id: typeof o.id === 'string' && o.id ? o.id : `t${i + 1}`,
    title,
    description: typeof o.description === 'string' ? o.description : '',
    dependsOn: asStrings(o.dependsOn),
    targets: asStrings(o.targets),
  };
}

/** Parse a planner reply into a {@link PlanResult} (defensive). */
export function parsePlanResult(raw: string): PlanResult {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) {
    return { kind: 'clarify', questions: ['The planner returned no usable JSON. Rephrase the task?'] };
  }
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return { kind: 'clarify', questions: ['The plan was not valid JSON. Rephrase the task?'] };
  }
  if (json.kind === 'clarify') {
    const questions = asStrings(json.questions);
    return { kind: 'clarify', questions: questions.length > 0 ? questions : ['Please clarify the task.'] };
  }
  const planObj = json.plan && typeof json.plan === 'object' ? (json.plan as Record<string, unknown>) : json;
  const rawTasks = Array.isArray(planObj.tasks) ? planObj.tasks : [];
  const tasks = rawTasks.map((t, i) => normalizeTask(t, i)).filter((t): t is Task => t !== null);
  if (tasks.length === 0) {
    return { kind: 'clarify', questions: ['Could not derive any tasks. Add detail to the request?'] };
  }
  return {
    kind: 'plan',
    plan: { summary: typeof planObj.summary === 'string' ? planObj.summary : '', tasks },
  };
}

/** Decompose `task` into a {@link PlanResult} using DeepSeek (thinking, Pro). */
export async function planFromIntent(task: string, context: string): Promise<PlanResult> {
  const ai = createAiClient({ provider: 'deepseek' });
  const reply = await ai.chat({
    system: PLANNER_SYSTEM,
    messages: [{ role: 'user', content: `CONTEXT:\n${context.slice(0, 6000)}\n\nTASK:\n${task}` }],
    reasoning: { effort: 'high' }, // thinking for a coherent plan
    model: DEEPSEEK_PRO_MODEL,
    maxTokens: 4000,
  });
  return parsePlanResult(reply);
}

// --- the model/effort-aware build plan ---------------------------------------

/** One research step the engine performs before building. */
export interface ResearchStep {
  readonly id: string;
  readonly question: string;
}

/** A decision area where the model proposes candidates; selection.ts decides. */
export interface ToolingArea {
  readonly area: string;
  readonly candidates: readonly string[];
}

/** The draft the model produces (before candidate ranking resolves choices). */
export interface BuildPlanDraft {
  readonly research: readonly ResearchStep[];
  readonly tooling: readonly ToolingArea[];
  readonly tasks: readonly Task[];
}

/**
 * The full pre-build plan: produced and surfaced BEFORE execution starts, then
 * checkpointed so the build executes against it (and it stays inspectable).
 */
export interface BuildPlan {
  readonly goal: SessionGoal;
  readonly model: string;
  readonly tier?: Tier;
  readonly effort: EffortLevel;
  readonly value: ValueAssessment;
  readonly budget: Budget;
  readonly research: readonly ResearchStep[];
  /** Resolved tooling choices with recorded rationale (auditable). */
  readonly tooling: readonly SelectionResult[];
  /** Packages the build expects to pull (the chosen candidates). */
  readonly packages: readonly string[];
  readonly tasks: readonly Task[];
}

const BUILD_PLAN_SYSTEM = [
  'You are a senior engineer planning HOW to build a task before any code is written.',
  'Respond with ONLY one minified JSON object — no prose, no code fence — shaped as',
  '{"research":[{"id":string,"question":string}],',
  '"tooling":[{"area":string,"candidates":string[]}],',
  '"tasks":[{"id":string,"title":string,"description":string,"dependsOn":string[],"targets":string[]}]}.',
  'research = what to investigate first (docs, existing code, APIs).',
  'tooling = decision areas where a package/framework must be chosen; list candidate npm package names.',
  'Prefer the most widely-used, well-maintained, well-documented mainstream options as candidates;',
  'propose a niche package only when the task genuinely requires it.',
  'tasks = the ordered build steps as a dependency DAG (short stable ids).',
].join('\n');

function normalizeResearch(v: unknown): ResearchStep[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((r, i) => {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const question = typeof o.question === 'string' ? o.question.trim() : '';
      if (!question) return null;
      return { id: typeof o.id === 'string' && o.id ? o.id : `r${i + 1}`, question };
    })
    .filter((r): r is ResearchStep => r !== null);
}

function normalizeTooling(v: unknown): ToolingArea[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((t): ToolingArea | null => {
      if (!t || typeof t !== 'object') return null;
      const o = t as Record<string, unknown>;
      const area = typeof o.area === 'string' ? o.area.trim() : '';
      // Dedupe BEFORE the per-budget clamp, so repeated names can't eat the
      // candidate slots.
      const candidates = [...new Set(asStrings(o.candidates))];
      if (!area || candidates.length === 0) return null;
      return { area, candidates };
    })
    .filter((t): t is ToolingArea => t !== null);
}

/**
 * Parse a build-plan reply, CLAMPING depth to the budget so the configured
 * effort→depth mapping holds even when the model over-produces.
 */
export function parseBuildPlanDraft(raw: string, budget: Budget): BuildPlanDraft | null {
  const start = raw.indexOf('{');
  const end = raw.lastIndexOf('}');
  if (start === -1 || end <= start) return null;
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return null;
  }
  const rawTasks = Array.isArray(json.tasks) ? json.tasks : [];
  const tasks = rawTasks
    .map((t, i) => normalizeTask(t, i))
    .filter((t): t is Task => t !== null)
    .slice(0, budget.maxPlanTasks);
  if (tasks.length === 0) return null;
  return {
    research: normalizeResearch(json.research).slice(0, budget.researchSteps),
    tooling: normalizeTooling(json.tooling).map((t) => ({
      area: t.area,
      candidates: t.candidates.slice(0, budget.candidatesPerChoice),
    })),
    tasks,
  };
}

/**
 * Draft the build plan from the goal. Depth knobs (how many research steps,
 * candidates, tasks) come from the budget; how much one step may batch comes
 * from the executing model's profile — both injected into the prompt, so a
 * higher effort/value visibly produces a deeper plan for the same task.
 */
export async function draftBuildPlan(
  goal: SessionGoal,
  context: string,
  ai: ChatClient,
  opts: {
    readonly model: string;
    readonly effort: EffortLevel;
    readonly budget: Budget;
    readonly modelProfile: ModelProfile;
    readonly maxTokens?: number;
  },
): Promise<BuildPlanDraft | null> {
  const { budget, modelProfile: mp } = opts;
  const knobs = [
    `Plan depth for this run: at most ${budget.researchSteps} research steps,`,
    `at most ${budget.candidatesPerChoice} candidates per tooling area,`,
    `at most ${budget.maxPlanTasks} tasks.`,
    `The executing model handles ${mp.stepComplexity} steps; batch up to ${mp.batchFactor}`,
    'closely-related subtasks into one task rather than splitting them.',
  ].join(' ');
  const reply = await ai.chat({
    system: `${BUILD_PLAN_SYSTEM}\n${knobs}`,
    messages: [
      {
        role: 'user',
        content:
          `CONTEXT:\n${context.slice(0, 6000)}\n\nGOAL: ${goal.statement}\n` +
          `DONE WHEN:\n${goal.criteria.map((c) => `- ${c.text}`).join('\n')}\n\nTASK:\n${goal.task}`,
      },
    ],
    reasoning: effortToReasoning(opts.effort),
    model: opts.model,
    maxTokens: opts.maxTokens ?? 4000,
  });
  return parseBuildPlanDraft(reply, budget);
}

/**
 * Topologically batch tasks by dependency tags: each returned batch may run
 * concurrently in a bounded pool; later batches depend on earlier ones. A cycle
 * or dangling dependency degrades gracefully (the remainder is emitted as one
 * final batch rather than looping forever).
 */
export function scheduleByDeps(tasks: readonly Task[]): Task[][] {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const remaining = new Set(tasks.map((t) => t.id));
  const done = new Set<string>();
  const batches: Task[][] = [];

  while (remaining.size > 0) {
    const ready: Task[] = [];
    for (const id of remaining) {
      const t = byId.get(id);
      if (t && t.dependsOn.every((d) => done.has(d) || !byId.has(d))) ready.push(t);
    }
    if (ready.length === 0) {
      const rest: Task[] = [];
      for (const id of remaining) {
        const t = byId.get(id);
        if (t) rest.push(t);
      }
      batches.push(rest);
      break;
    }
    batches.push(ready);
    for (const t of ready) {
      remaining.delete(t.id);
      done.add(t.id);
    }
  }
  return batches;
}
